import React, { FC, useCallback, useEffect, useState } from 'react';
import { Alert, MenuItem, Paper, TextField, Typography } from '@mui/material';
import styled from 'styled-components';
import EdButton from '@Components/UI/Buttons/EdButton/EdButton';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import { useSnackbar } from '@Providers/useSnackbar';
import { useAppThunkDispatch } from '@Features/store';
import { fetchSettings } from '@Features/settings/settingsSlice';
import { FlexLayout } from '@Styled/utilities';
import YCloudCard from './YCloudCard';
import {
	WhatsappSettingsRequester,
	whatsappErrorMessage,
} from './whatsapp.requester';
import {
	WhatsappProviderSetting,
	WhatsappQueueStats,
	WhatsappSettingsResponse,
} from './whatsapp.types';

const requester = WhatsappSettingsRequester.getInstance();

/** Retry-all-failed windows offered; the backend caps at one week (168 h). */
const PERIODS = [
	{ hours: 1, label: 'Last hour' },
	{ hours: 24, label: 'Last 24 hours' },
	{ hours: 72, label: 'Last 3 days' },
	{ hours: 168, label: 'Last 7 days' },
];

/**
 * Settings -> WhatsApp. Isolated from app_settings by design (that row is
 * publicly served): credentials live encrypted in whatsapp_provider_settings
 * and are write-only through the provider card. The card's Enabled switch is
 * the feature's master toggle — both portals follow it.
 */
const WhatsappTab = () => {
	const dispatch = useAppThunkDispatch();
	const [settings, setSettings] = useState<WhatsappSettingsResponse>();
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		try {
			const {
				data: { data },
			} = await requester.getAll();
			setSettings(data);
			setError(null);
		} catch (e: any) {
			setError(whatsappErrorMessage(e, 'Unable to load WhatsApp settings'));
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const onSaved = (fresh: WhatsappSettingsResponse) => {
		setSettings(fresh);
		// The master toggle lives in the public settings both portals read;
		// refresh so the Test Notifications tab and reports follow immediately.
		dispatch(fetchSettings());
	};

	const encryptionReady =
		!settings || settings.providers.every((row) => row.encryption_ready);
	const anyEnabled = !!settings?.providers.some((row) => row.enabled);
	const ycloud = settings?.providers.find((row) => row.provider === 'ycloud');

	return (
		<Container>
			{error && <Alert severity="error">{error}</Alert>}
			{!encryptionReady && (
				<Alert severity="warning">
					VIDEO_PROVIDERS_ENCRYPTION_KEY is not set on this server — provider
					secrets cannot be stored until it is configured on both backends.
				</Alert>
			)}
			{settings && ycloud && (
				<>
					<ChannelStatus setting={ycloud} queue={settings.queue} onChanged={onSaved} />
					<CardsLayout>
						<YCloudCard
							setting={ycloud}
							variables={settings.variables}
							attendanceVariables={settings.attendance_variables ?? []}
							onSaved={onSaved}
						/>
						<DeliverySection enabled={anyEnabled} />
					</CardsLayout>
				</>
			)}
		</Container>
	);
};

const DEFAULT_LOW_BALANCE_THRESHOLD = 10;

const formatWhen = (iso?: string | null) =>
	iso ? new Date(iso).toLocaleString() : '—';

const formatAge = (iso?: string | null) => {
	if (!iso) return '';
	const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
	if (minutes < 60) return `${minutes} min`;
	const hours = Math.round(minutes / 60);
	return hours < 48 ? `${hours} h` : `${Math.round(hours / 24)} d`;
};

/**
 * The channel's operational state, above the cards: the dispatcher's
 * circuit-breaker pause (account-level rejection — no balance, bad key,
 * unapproved template) with the queued backlog and a Resume-now shortcut,
 * and the low-balance warning that should fire before a pause ever does.
 */
const ChannelStatus: FC<{
	setting: WhatsappProviderSetting;
	queue?: WhatsappQueueStats;
	onChanged: (response: WhatsappSettingsResponse) => void;
}> = ({ setting, queue, onChanged }) => {
	const { displaySnackbar } = useSnackbar();
	const [resuming, setResuming] = useState(false);
	const paused = setting.config.paused ?? null;
	const balance = setting.config.balance ?? null;
	const threshold = setting.config.low_balance_threshold ?? DEFAULT_LOW_BALANCE_THRESHOLD;

	const resume = async () => {
		setResuming(true);
		try {
			const {
				data: { data },
			} = await requester.resume(setting.provider);
			onChanged(data);
			displaySnackbar('success', 'Channel resumed — the next dispatch cycle sends normally');
		} catch (error: any) {
			displaySnackbar('error', whatsappErrorMessage(error, 'Unable to resume the channel'));
		} finally {
			setResuming(false);
		}
	};

	if (!setting.enabled) return null;

	const lowBalance =
		balance?.amount !== null && balance?.amount !== undefined && balance.amount < threshold;

	return (
		<>
			{paused && (
				<Alert
					severity="error"
					action={
						<EdButton
							variant="outlined"
							edcolor="purple"
							disabled={resuming}
							onClick={resume}
							style={{ whiteSpace: 'nowrap' }}
						>
							{resuming ? 'Resuming…' : 'Resume now'}
						</EdButton>
					}
				>
					<strong>Sending is paused</strong> since {formatWhen(paused.since)} —{' '}
					{paused.code}
					{paused.message ? `: ${paused.message}` : ''}.{' '}
					{queue
						? `${queue.pending} message(s) waiting${
								queue.oldest_pending_at ? ` (oldest ${formatAge(queue.oldest_pending_at)})` : ''
						  }. `
						: ''}
					The dispatcher retries by itself with one message at {formatWhen(paused.until)}
					{paused.trips > 1 ? ` (attempt ${paused.trips})` : ''}; fix the cause — usually a
					top-up — and use Resume now to skip the wait. Nothing is lost: queued messages
					are sent once the account is healthy.
				</Alert>
			)}
			{!paused && lowBalance && (
				<Alert severity="warning">
					<strong>Low YCloud balance:</strong> {balance!.amount} {balance!.currency ?? ''} (warning
					level {threshold}). Top up before it runs out — an empty account pauses sending
					until it is recharged.
				</Alert>
			)}
		</>
	);
};

/**
 * Operational recovery: WhatsApp messages that gave up (`failed`) can be
 * re-queued in bulk for a bounded period once the cause (template, key,
 * balance) is fixed. WhatsApp-scoped end to end — the count and the button
 * cover the same rows, and the per-message resend in the test report is
 * WhatsApp-only too. SMS is one-shot by design (user decision): a failed
 * SMS is recorded with its gateway code but never retried.
 */
const DeliverySection: FC<{ enabled: boolean }> = ({ enabled }) => {
	const { displaySnackbar } = useSnackbar();
	const [hours, setHours] = useState(24);
	const [failed, setFailed] = useState<number | null>(null);
	const [confirming, setConfirming] = useState(false);
	const [busy, setBusy] = useState(false);

	const refreshCount = useCallback(async () => {
		try {
			const {
				data: { data },
			} = await requester.failedCount(hours);
			setFailed(data.failed);
		} catch {
			setFailed(null);
		}
	}, [hours]);

	useEffect(() => {
		refreshCount();
	}, [refreshCount]);

	const retry = async () => {
		setBusy(true);
		try {
			const {
				data: { data },
			} = await requester.retryFailed(hours);
			displaySnackbar('success', `Re-queued ${data.requeued} message(s)`);
			await refreshCount();
		} catch (error: any) {
			displaySnackbar('error', whatsappErrorMessage(error, 'Unable to retry failed messages'));
		} finally {
			setBusy(false);
		}
	};

	const periodLabel = PERIODS.find((period) => period.hours === hours)?.label ?? '';

	return (
		<DeliveryShell>
			<Typography variant="h6">Delivery</Typography>
			<Typography variant="caption" color="textSecondary">
				WhatsApp messages that could not be sent are marked failed with the
				provider’s reason (shown in each test report). Re-queue them here
				once the cause is fixed. SMS is one-shot — a failed SMS is recorded
				with its reason but not retried.
			</Typography>
			<FlexLayout alignItems="center" gridGap="1rem" flexWrap="wrap">
				<TextField
					select
					size="small"
					label="Period"
					value={hours}
					onChange={(event) => setHours(Number(event.target.value))}
					sx={{ minWidth: 180 }}
				>
					{PERIODS.map((period) => (
						<MenuItem key={period.hours} value={period.hours}>
							{period.label}
						</MenuItem>
					))}
				</TextField>
				<Typography variant="body2">
					{failed === null ? '—' : `${failed} failed message(s)`}
				</Typography>
				<EdButton
					variant="contained"
					edcolor="purple"
					disabled={!enabled || !failed || busy}
					onClick={() => setConfirming(true)}
				>
					Retry all failed
				</EdButton>
			</FlexLayout>
			{!enabled && (
				<Typography variant="caption" color="textSecondary">
					Enable a provider above to retry messages.
				</Typography>
			)}
			<EdConfirmModal
				open={confirming}
				text={`Re-queue ${failed ?? 0} failed message(s) from the ${periodLabel.toLowerCase()}? Each will be sent again on the next dispatch cycle.`}
				handleClose={(close, confirm) => {
					setConfirming(false);
					if (confirm) retry();
				}}
			/>
		</DeliveryShell>
	);
};

const Container = styled.div`
	padding: 2rem;
	display: flex;
	flex-direction: column;
	gap: 2rem;

	/* The app theme absolutely positions helper text under its field — a
	   one-line-helper convention. This tab writes sentence-length helpers
	   (the low-balance note, the mapping table's hints), which painted OVER
	   whatever came next; let them flow in normal layout here. */
	.MuiFormHelperText-root {
		position: static;
	}
`;

const CardsLayout = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
	gap: 1.5rem;
	align-items: start;

	@media (max-width: 1200px) {
		grid-template-columns: 1fr;
	}
`;

const DeliveryShell = styled(Paper)`
	padding: 1.25rem 1.5rem;
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

export default WhatsappTab;
